import { useEffect, useState, useSyncExternalStore } from "react";
import { fetchFeatureFlags, getCachedFlags, subscribeToFeatureFlags } from "./featureFlagsService";
import { FeatureFlagName } from "./types";

const NO_FLAGS: Readonly<Record<string, boolean>> = Object.freeze({});

interface FetchOutcome {
  flags: Record<string, boolean> | null;
  error: string | null;
}

export const useFeatureFlags = () => {
  // Shared across consumers, so values that land after this component mounted
  // (a slow first request, a background refresh, the last-known-good fallback)
  // re-render it instead of leaving it on whatever it saw first.
  const cached = useSyncExternalStore(subscribeToFeatureFlags, getCachedFlags);
  const [outcome, setOutcome] = useState<FetchOutcome | null>(null);

  useEffect(() => {
    let mounted = true;

    fetchFeatureFlags().then(
      (flags) => {
        if (mounted) setOutcome({ flags, error: null });
      },
      (err: unknown) => {
        if (mounted) {
          setOutcome({
            flags: null,
            error: err instanceof Error ? err.message : "Failed to load feature flags",
          });
        }
      }
    );

    return () => {
      mounted = false;
    };
  }, []);

  const flags = cached ?? outcome?.flags ?? NO_FLAGS;
  return {
    flags,
    isLoading: cached === null && outcome === null,
    // Flags that land after a failed first fetch supersede that failure.
    error: cached === null ? (outcome?.error ?? null) : null,
  };
};

export const useFeatureFlag = (
  flagName: FeatureFlagName
): { value: boolean; isLoading: boolean } => {
  const { flags, isLoading } = useFeatureFlags();
  return { value: flags[flagName] ?? false, isLoading };
};
